import React from 'react';
import { cities } from '../../data/citiesData';
import { 
  Box, 
  Heading, 
  Input, 
  Select, 
  VStack, 
  useColorModeValue,
  Icon,
  Divider,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper
} from '@chakra-ui/react';
import { 
  FaFilter, 
  FaHome, 
  FaMapMarkerAlt, 
  FaBuilding, 
  FaDollarSign,
  FaCity
} from 'react-icons/fa';

// Property types matching HeroSection with Pakistani terminology
const propertyTypes = [
  { id: '', name: 'All Types' },
  // Residential
  { id: 'lower-portion', name: 'Lower Portion', group: 'Residential' },
  { id: 'upper-portion', name: 'Upper Portion', group: 'Residential' },
  { id: 'full-house', name: 'Full House', group: 'Residential' },
  { id: 'farmhouse', name: 'Farmhouse', group: 'Residential' },
  { id: 'banglow', name: 'Banglow', group: 'Residential' },
  { id: 'apartment', name: 'Apartment', group: 'Residential' },
  // Plots & Land
  { id: 'residential-plot', name: 'Residential Plot', group: 'Plots & Land' },
  { id: 'commercial-plot', name: 'Commercial Plot', group: 'Plots & Land' },
  { id: 'agricultural-land', name: 'Agricultural Land', group: 'Plots & Land' },
  // Commercial
  { id: 'shop', name: 'Shop', group: 'Commercial' },
  { id: 'office', name: 'Office', group: 'Commercial' },
  { id: 'godown', name: 'Godown/Warehouse', group: 'Commercial' },
  { id: 'showroom', name: 'Showroom', group: 'Commercial' },
  { id: 'restaurant', name: 'Restaurant/Cafe', group: 'Commercial' },
  { id: 'hotel', name: 'Hotel/Guest House', group: 'Commercial' },
  { id: 'plaza', name: 'Commercial Plaza', group: 'Commercial' }
];

// Group property types for organized display
const groupedPropertyTypes = {
  'Residential': propertyTypes.filter(p => p.group === 'Residential'),
  'Plots & Land': propertyTypes.filter(p => p.group === 'Plots & Land'),
  'Commercial': propertyTypes.filter(p => p.group === 'Commercial')
};

const Sidebar = ({ 
  propertyType, 
  city, 
  location,
  listingType,
  priceRange, 
  setPropertyType, 
  setCity, 
  setLocation,
  setListingType,
  setPriceRange,
  minPrice,
  maxPrice,
  setMinPrice,
  setMaxPrice
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const greenColor = useColorModeValue('green.500', 'green.300');
  const lightGreenBg = useColorModeValue('green.50', 'green.900');

  // Handle price range changes
  const handleMinPriceChange = (value) => {
    setMinPrice(value);
    // Update the combined priceRange if needed
    if (value && maxPrice) {
      setPriceRange(`${value}-${maxPrice}`);
    } else if (value) {
      setPriceRange(`${value}+`);
    } else if (maxPrice) {
      setPriceRange(`0-${maxPrice}`);
    } else {
      setPriceRange('');
    }
  };

  const handleMaxPriceChange = (value) => {
    setMaxPrice(value);
    // Update the combined priceRange if needed
    if (minPrice && value) {
      setPriceRange(`${minPrice}-${value}`);
    } else if (value) {
      setPriceRange(`0-${value}`);
    } else if (minPrice) {
      setPriceRange(`${minPrice}+`);
    } else {
      setPriceRange('');
    }
  };

  // Check if any filter is active
  const hasActiveFilters = propertyType || city || location || listingType !== 'sale' || minPrice || maxPrice;

  return (
    <Box
      w="280px"
      minW="280px"
      h="calc(100vh - 100px)"
      maxH="calc(100vh - 100px)"
      bg={bgColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="md"
      position="sticky"
      top="20px"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      {/* Scrollable Content */}
      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        sx={{
          '&::-webkit-scrollbar': {
            width: '8px',
            borderRadius: '8px',
            backgroundColor: `rgba(0, 0, 0, 0.05)`,
          },
          '&::-webkit-scrollbar-track': {
            width: '8px',
            borderRadius: '8px',
            backgroundColor: `rgba(0, 0, 0, 0.05)`,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: `rgba(0, 0, 0, 0.2)`,
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: `rgba(0, 0, 0, 0.3)`,
            },
          },
        }}
      >
        <VStack align="flex-start" spacing={6} p={6}>
          {/* Header */}
          <Box w="full">
            <Heading size="lg" display="flex" alignItems="center" color={textColor}>
              <Icon as={FaFilter} mr={2} color={greenColor} />
              Filters
            </Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Find your perfect property in Pakistan
            </Text>
            <Divider my={4} borderColor={borderColor} />
          </Box>

          {/* Listing Type Toggle */}
          <Box w="full">
            <Heading size="sm" mb={2} display="flex" alignItems="center" color={textColor}>
              <Icon as={FaBuilding} mr={2} color={greenColor} />
              Listing Type
            </Heading>
            <Box display="flex" gap={2}>
              <Box
                as="button"
                flex={1}
                py={2}
                px={3}
                borderRadius="md"
                fontSize="sm"
                fontWeight="medium"
                bg={listingType === 'sale' ? greenColor : bgColor}
                color={listingType === 'sale' ? 'white' : textColor}
                border="1px solid"
                borderColor={listingType === 'sale' ? greenColor : borderColor}
                _hover={{
                  bg: listingType === 'sale' ? greenColor : hoverBg
                }}
                onClick={() => setListingType('sale')}
              >
                For Sale
              </Box>
              <Box
                as="button"
                flex={1}
                py={2}
                px={3}
                borderRadius="md"
                fontSize="sm"
                fontWeight="medium"
                bg={listingType === 'rent' ? greenColor : bgColor}
                color={listingType === 'rent' ? 'white' : textColor}
                border="1px solid"
                borderColor={listingType === 'rent' ? greenColor : borderColor}
                _hover={{
                  bg: listingType === 'rent' ? greenColor : hoverBg
                }}
                onClick={() => setListingType('rent')}
              >
                For Rent
              </Box>
            </Box>
          </Box>

          {/* Property Type Filter */}
          <Box w="full">
            <Heading size="sm" mb={2} display="flex" alignItems="center" color={textColor}>
              <Icon as={FaHome} mr={2} color={greenColor} />
              Property Type
            </Heading>
            <Select
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value)}
              bg={bgColor}
              borderColor={borderColor}
              _hover={{ borderColor: 'green.300' }}
              _focus={{
                borderColor: greenColor,
                boxShadow: `0 0 0 1px ${greenColor}`,
              }}
              focusBorderColor={greenColor}
            >
              <option value="">All Types</option>
              <optgroup label="🏠 Residential">
                {groupedPropertyTypes['Residential'].map((type) => (
                  <option 
                    key={type.id} 
                    value={type.id}
                    style={{ 
                      backgroundColor: bgColor,
                      color: textColor
                    }}
                  >
                    {type.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="📐 Plots & Land">
                {groupedPropertyTypes['Plots & Land'].map((type) => (
                  <option 
                    key={type.id} 
                    value={type.id}
                    style={{ 
                      backgroundColor: bgColor,
                      color: textColor
                    }}
                  >
                    {type.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="🏢 Commercial">
                {groupedPropertyTypes['Commercial'].map((type) => (
                  <option 
                    key={type.id} 
                    value={type.id}
                    style={{ 
                      backgroundColor: bgColor,
                      color: textColor
                    }}
                  >
                    {type.name}
                  </option>
                ))}
              </optgroup>
            </Select>
          </Box>

          {/* City Filter */}
          <Box w="full">
            <Heading size="sm" mb={2} display="flex" alignItems="center" color={textColor}>
              <Icon as={FaCity} mr={2} color={greenColor} />
              City
            </Heading>
            <Select
              placeholder="All Cities"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              bg={bgColor}
              borderColor={borderColor}
              _hover={{ borderColor: 'green.300' }}
              _focus={{
                borderColor: greenColor,
                boxShadow: `0 0 0 1px ${greenColor}`,
              }}
              focusBorderColor={greenColor}
            >
              {cities.map((cityOption) => (
                <option 
                  key={cityOption.id} 
                  value={cityOption.name}
                  style={{ 
                    backgroundColor: bgColor,
                    color: textColor
                  }}
                >
                  {cityOption.name}
                </option>
              ))}
            </Select>
          </Box>

          {/* Location/Area Filter */}
          <Box w="full">
            <Heading size="sm" mb={2} display="flex" alignItems="center" color={textColor}>
              <Icon as={FaMapMarkerAlt} mr={2} color={greenColor} />
              Area / Society / Landmark
            </Heading>
            <Input
              placeholder="e.g., B-17, DHA, Gulshan"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              bg={bgColor}
              borderColor={borderColor}
              _hover={{ borderColor: 'green.300' }}
              _focus={{
                borderColor: greenColor,
                boxShadow: `0 0 0 1px ${greenColor}`,
              }}
              focusBorderColor={greenColor}
            />
          </Box>

          {/* Price Range Filter */}
          <Box w="full">
            <Heading size="sm" mb={2} display="flex" alignItems="center" color={textColor}>
              <Icon as={FaDollarSign} mr={2} color={greenColor} />
              Price Range (PKR)
            </Heading>
            <VStack spacing={2}>
              <NumberInput
                value={minPrice}
                onChange={handleMinPriceChange}
                min={0}
                width="full"
              >
                <NumberInputField
                  placeholder={`Min Price (${listingType === 'sale' ? 'Sale' : 'Rent'})`}
                  bg={bgColor}
                  borderColor={borderColor}
                  _hover={{ borderColor: 'green.300' }}
                  _focus={{
                    borderColor: greenColor,
                    boxShadow: `0 0 0 1px ${greenColor}`,
                  }}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              
              <NumberInput
                value={maxPrice}
                onChange={handleMaxPriceChange}
                min={0}
                width="full"
              >
                <NumberInputField
                  placeholder="Max Price"
                  bg={bgColor}
                  borderColor={borderColor}
                  _hover={{ borderColor: 'green.300' }}
                  _focus={{
                    borderColor: greenColor,
                    boxShadow: `0 0 0 1px ${greenColor}`,
                  }}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </VStack>
          </Box>

          {/* Reset Button */}
          {hasActiveFilters && (
            <Box w="full" pt={4}>
              <Box
                as="button"
                w="full"
                py={2}
                px={4}
                borderRadius="md"
                fontWeight="medium"
                bg={lightGreenBg}
                color={greenColor}
                border="1px solid"
                borderColor={greenColor}
                _hover={{ 
                  bg: greenColor,
                  color: 'white'
                }}
                onClick={() => {
                  setPropertyType('');
                  setCity('');
                  setLocation('');
                  setListingType('sale');
                  setMinPrice('');
                  setMaxPrice('');
                  setPriceRange('');
                }}
              >
                Clear All Filters
              </Box>
            </Box>
          )}
          
          {/* Add extra bottom padding for better scroll experience */}
          <Box h="2" />
        </VStack>
      </Box>
    </Box>
  );
};

export default Sidebar;